import type { MutableRefObject } from 'react';
import { terraProxyApiClient } from '../clients/testerClient';
import { Logger } from '../electron/logger';

export type FindingEditSignalSearchResult = {
  id: string;
  label: string;
  category?: string;
  description?: string;
  status?: string;
  createdAt?: string;
};

type FindingSignalEnrichSource = {
  id?: string | null;
  description?: string | null;
} | null;

type FindingSignalSummaryEnrichSource = {
  title?: string | null;
} | null;

const logger = new Logger('findingEditSignalLabelEnrichment');

export function resolveFindingEditSignalSeedLabel({
  signalId,
  signalDescription,
  signalSummaryTitle,
}: {
  signalId: string;
  signalDescription?: string | null;
  signalSummaryTitle?: string | null;
}): string {
  const descriptionLabel = signalDescription?.trim().slice(0, 80);
  if (descriptionLabel) {
    return descriptionLabel;
  }

  const summaryLabel = signalSummaryTitle?.trim().slice(0, 80);
  if (summaryLabel) {
    return summaryLabel;
  }

  return signalId.slice(0, 8);
}

export function resolveFindingEditSignalIdToEnrich({
  signal,
  signalId,
  signalSummary,
}: {
  signal?: FindingSignalEnrichSource;
  signalId?: string | null;
  signalSummary?: FindingSignalSummaryEnrichSource;
}): string | null {
  const resolvedSignalId = signal?.id ?? signalId ?? null;

  if (
    !resolvedSignalId ||
    signal?.description?.trim() ||
    signalSummary?.title?.trim()
  ) {
    return null;
  }

  return resolvedSignalId;
}

export function toFindingEditSignalSearchResult({
  id,
  description,
  category,
  status,
  createdAt,
}: {
  id: string;
  description?: string | null;
  category?: string | null;
  status?: string | null;
  createdAt?: string | Date | null;
}): FindingEditSignalSearchResult {
  const createdAtValue =
    typeof createdAt === 'string'
      ? createdAt
      : createdAt?.toISOString?.() || undefined;

  return {
    id,
    label: description?.slice(0, 80) || id.slice(0, 8),
    category: category || undefined,
    description: description || undefined,
    status: status || undefined,
    createdAt: createdAtValue,
  };
}

export function mergeEnrichedSignalIntoSearchResults({
  previous,
  enriched,
}: {
  previous: FindingEditSignalSearchResult[];
  enriched: FindingEditSignalSearchResult;
}): FindingEditSignalSearchResult[] {
  if (previous.length === 0) {
    return [enriched];
  }

  if (previous.some((entry) => entry.id === enriched.id)) {
    return previous.map((entry) =>
      entry.id === enriched.id ? { ...entry, ...enriched } : entry,
    );
  }

  return previous;
}

export function isStaleFindingEditSignalEnrichment({
  activeFindingId,
  expectedFindingId,
  expectedSignalId,
  selectedSignalId,
  fetchedSignalId,
}: {
  activeFindingId: string | null;
  expectedFindingId: string | null | undefined;
  expectedSignalId: string;
  selectedSignalId: string | null;
  fetchedSignalId: string;
}): boolean {
  return (
    activeFindingId !== expectedFindingId ||
    fetchedSignalId !== expectedSignalId ||
    selectedSignalId !== fetchedSignalId
  );
}

export async function enrichFindingEditSignalLabel({
  signalId,
  findingId,
  activeFindingIdRef,
  selectedSignalIdRef,
  setSignalDisplayById,
  setSignalSearchResults,
  isCancelled,
}: {
  signalId: string;
  findingId: string | null | undefined;
  activeFindingIdRef: MutableRefObject<string | null>;
  selectedSignalIdRef: MutableRefObject<string | null>;
  setSignalDisplayById: (
    value: Record<string, FindingEditSignalSearchResult>,
  ) => void;
  setSignalSearchResults: (
    updater: (
      previous: FindingEditSignalSearchResult[],
    ) => FindingEditSignalSearchResult[],
  ) => void;
  isCancelled: () => boolean;
}): Promise<void> {
  try {
    const signal = await terraProxyApiClient.getSignal(signalId);

    if (isCancelled()) {
      return;
    }

    if (
      isStaleFindingEditSignalEnrichment({
        activeFindingId: activeFindingIdRef.current,
        expectedFindingId: findingId,
        expectedSignalId: signalId,
        selectedSignalId: selectedSignalIdRef.current,
        fetchedSignalId: signal.id,
      })
    ) {
      return;
    }

    const enriched = toFindingEditSignalSearchResult(signal);

    setSignalDisplayById({ [signal.id]: enriched });
    setSignalSearchResults((previous) =>
      mergeEnrichedSignalIntoSearchResults({ previous, enriched }),
    );
  } catch (error: unknown) {
    logger.warn('Failed to load signal label for finding edit', {
      findingId,
      signalId,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}
